import {Component, inject, input, OnInit, output, viewChild} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewMailUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';

@Component({
  selector: 'app-step-9-2-1-rfc-mail-preview',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-9-2-1-rfc-mail-preview.component.html',
})
export class Step921RfcMailPreviewComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly invitationPreviewController = inject(TenderInvitationPreviewControllerService);
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );

  readonly preloadedData = input<TenderInvitationPreviewResponse | undefined>(undefined);
  readonly stepLocked = input<boolean>(false);
  readonly statusChange = output<TabStatus>();

  readonly richTextEditor = viewChild(RichTextEditorComponent);

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get mailSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: RFC_MAIL_PREVIEW_SECTIONS,
      fieldMap: RFC_MAIL_PREVIEW_FIELD_MAP,
      saveFunction: (projectId, payload) => this.save(projectId, payload),
      loadFunction: (projectId) =>
        this.invitationPreviewController.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFC,
        }),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private save(
    projectId: number,
    richTextPayload: TenderInvitationPreviewMailUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    return this.invitationPreviewController
      .getTenderInvitationPreview({
        projectId,
        phase: TenderPhaseType.RFC,
      })
      .pipe(
        switchMap((existingData) => {
          const completePayload: TenderInvitationPreviewMailUpsertRequest = {
            ...existingData,
            ...richTextPayload,
            emailSubject: this.richTextEditor()?.currentLabel() || existingData.emailSubject,
          };
          return this.invitationPreviewController.upsertMail({
            projectId,
            phase: TenderPhaseType.RFC,
            tenderInvitationPreviewMailUpsertRequest: completePayload,
          });
        }),
      );
  }

  getEmailSubjectForSection(): string {
    return this.preloadedData()?.emailSubject || '';
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();

    this.statusChange.emit({totalSections, completedSections});
  }
}

export const RFC_MAIL_PREVIEW_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfc_project_decision_mail_preview',
    content: '',
    tooltipKey: '',
    hasChanges: false,
  },
];

export const RFC_MAIL_PREVIEW_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewMailUpsertRequest
> = {
  step_rfc_project_decision_mail_preview: 'mailText',
};
